import { toast } from '@loopreturns/sonik/toast';
import { enablePageScroll } from 'scroll-lock';
import { watch } from 'vue';
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';

import { applyDocumentTitle, isShopDetailRoute, resolveShopTitleContext } from '@/helpers/pageTitle';
import { authNavigationGuard } from '@/router/authGuard';
import { ROUTE_NAMES } from '@/router/routeNames';
import { useShopStore } from '@/stores/shop';

export { ROUTE_NAMES };

function loadView (view: string) {
  return () => import(`@/views/${view}.vue`);
}

const routes: RouteRecordRaw[] = [
  {
    path: '/',
    name: ROUTE_NAMES.STARTUP,
    component: loadView('Startup'),
    meta: { public: true, title: 'Login' },
  },
  {
    path: '/login',
    redirect: { name: ROUTE_NAMES.STARTUP },
  },
  {
    path: '/logout',
    redirect: { name: ROUTE_NAMES.STARTUP },
  },
  {
    path: '/shops',
    name: ROUTE_NAMES.HOME,
    component: loadView('Home'),
    meta: { title: 'Shops' },
  },
  {
    path: '/gdpr-data',
    name: ROUTE_NAMES.GDPR,
    component: loadView('GDPRPage'),
    meta: { title: 'Redact GDPR Data' },
  },
  {
    path: '/demo-shops',
    name: ROUTE_NAMES.DEMO_SHOPS,
    component: loadView('DemoShopsPage'),
  },
  {
    path: '/demo-shops/history',
    name: ROUTE_NAMES.DEMO_SHOPS_HISTORY,
    component: loadView('DemoShopsHistoryPage'),
    meta: { title: 'Former Demo Mode Shops' },
  },
  {
    path: '/loop-anywhere-setup',
    name: ROUTE_NAMES.LOOP_ANYWHERE_SETUP,
    component: loadView('LoopAnywhereSetupPage'),
    meta: { title: 'Loop Anywhere Setup' },
  },
  {
    path: '/returns',
    name: ROUTE_NAMES.RETURNS,
    component: loadView('Return'),
    meta: { title: 'Search Returns' },
  },
  {
    path: '/returns/:id',
    component: loadView('ReturnDetail'),
    meta: { layout: 'detail' },
    children: [
      { path: '', redirect: { name: ROUTE_NAMES.RETURN_ACCOUNTING } },
      { path: 'accounting', name: ROUTE_NAMES.RETURN_ACCOUNTING, component: loadView('ReturnAccounting'), meta: { title: 'Return Details' } },
      { path: 'timeline', name: ROUTE_NAMES.RETURN_TIMELINE, component: loadView('ReturnTimeline'), meta: { title: 'Timeline' } },
      { path: 'raw', name: ROUTE_NAMES.RETURN_RAW, component: loadView('ReturnRaw'), meta: { title: 'Raw Data' } },
      { path: 'settings', name: ROUTE_NAMES.RETURN_SETTINGS, component: loadView('ReturnSettings'), meta: { title: 'Settings' } },
      { path: 'formatted', name: ROUTE_NAMES.RETURN_FORMATTED, component: loadView('ReturnFormatted'), meta: { title: 'Formatted Data' } },
      { path: 'ledger', name: ROUTE_NAMES.RETURN_LEDGER, component: loadView('ReturnLedger'), meta: { title: 'Ledger' } },
      { path: 'logs', name: ROUTE_NAMES.RETURN_LOGS, component: loadView('ReturnLogs'), meta: { title: 'Logs' } },
      { path: 'workflow-logs', name: ROUTE_NAMES.RETURN_WORKFLOW_LOGS, component: loadView('OrderWorkflowLogs'), meta: { title: 'Workflow Logs' } },
    ],
  },
  {
    path: '/orders/:id',
    component: loadView('OrderDetail'),
    meta: { layout: 'detail' },
    children: [
      { path: '', redirect: { name: ROUTE_NAMES.ORDER_ACCOUNTING } },
      { path: 'accounting', name: ROUTE_NAMES.ORDER_ACCOUNTING, component: loadView('OrderAccounting'), meta: { title: 'Order Detail' } },
      { path: 'raw', name: ROUTE_NAMES.ORDER_RAW, component: loadView('OrderRaw'), meta: { title: 'Raw Data' } },
      { path: 'formatted', name: ROUTE_NAMES.ORDER_FORMATTED, component: loadView('OrderFormatted'), meta: { title: 'Formatted Data' } },
      { path: 'workflow-logs', name: ROUTE_NAMES.ORDER_WORKFLOW_LOGS, component: loadView('OrderWorkflowLogs'), meta: { title: 'Workflow Logs' } },
    ],
  },
  {
    path: '/workflow-logs',
    name: ROUTE_NAMES.WORKFLOW_LOGS,
    component: loadView('WorkflowLogs'),
    meta: { title: 'Search Workflow Logs' },
  },
  {
    path: '/loop-assistant',
    name: ROUTE_NAMES.LOOP_ASSISTANT,
    component: loadView('LoopAssistantPage'),
    meta: { title: 'Loop Assistant' },
  },
  {
    path: '/loop-assistant/sessions/:sessionId',
    name: ROUTE_NAMES.LOOP_ASSISTANT_SESSION,
    component: loadView('LoopAssistantSessionPage'),
    meta: { title: 'Loop Assistant Session' },
  },
  {
    path: '/loop-assistant/knowledge-snippets',
    name: ROUTE_NAMES.KNOWLEDGE_SNIPPETS,
    component: loadView('KnowledgeSnippetsPage'),
    meta: { title: 'Knowledge Snippets' },
  },
  {
    path: '/loop-assistant/knowledge-snippets/create',
    name: ROUTE_NAMES.KNOWLEDGE_SNIPPET_CREATE,
    component: loadView('KnowledgeSnippetCreatePage'),
    meta: { title: 'Create Knowledge Snippet' },
  },
  {
    path: '/loop-assistant/knowledge-snippets/:id/edit',
    name: ROUTE_NAMES.KNOWLEDGE_SNIPPET_EDIT,
    component: loadView('KnowledgeSnippetEditPage'),
    meta: { title: 'Edit Knowledge Snippet' },
  },
  {
    path: '/background-agents',
    name: ROUTE_NAMES.BACKGROUND_AGENTS_FLEET,
    component: loadView('BackgroundAgentsFleetPage'),
    meta: { title: 'Background Agents' },
  },
  {
    path: '/experiments',
    name: ROUTE_NAMES.EXPERIMENTS,
    component: loadView('ExperimentsPage'),
    meta: { title: 'Experiments' },
  },
  {
    path: '/experiments/new',
    name: ROUTE_NAMES.EXPERIMENT_CREATE,
    component: loadView('ExperimentCreatePage'),
    meta: { title: 'New experiment' },
  },
  {
    path: '/experiments/:key',
    name: ROUTE_NAMES.EXPERIMENT_DETAIL,
    component: loadView('ExperimentDetailPage'),
    meta: { title: 'Experiment' },
  },
  {
    path: '/shipping',
    name: ROUTE_NAMES.SHIPPING,
    component: loadView('ShippingPage'),
    meta: { title: 'Shipping Detail Lookup' },
  },
  {
    path: '/sendcloud',
    name: ROUTE_NAMES.SENDCLOUD,
    component: loadView('Sendcloud'),
    meta: { title: 'Sendcloud' },
  },
  {
    path: '/shops/:id',
    component: loadView('Shop'),
    meta: { layout: 'detail' },
    children: [
      { path: '', redirect: { name: ROUTE_NAMES.SHOP_HOME } },
      { path: 'details', name: ROUTE_NAMES.SHOP_HOME, component: loadView('GeneralPage'), meta: { title: 'Details' } },
      { path: 'plans', name: ROUTE_NAMES.SHOP_PLANS, component: loadView('ShopPlansPage'), meta: { title: 'Plans' } },
      { path: 'settings', name: ROUTE_NAMES.SHOP_SETTINGS, component: loadView('SettingPage'), meta: { title: 'Settings' } },
      { path: 'locations', name: ROUTE_NAMES.SHOP_LOCATIONS, component: loadView('LocationsPage'), meta: { title: 'Locations' } },
      { path: 'features', name: ROUTE_NAMES.SHOP_FEATURES, component: loadView('FeaturesPage'), meta: { title: 'Features' } },
      { path: 'taxonomy', name: ROUTE_NAMES.SHOP_TAXONOMY, component: loadView('TaxonomyPage'), meta: { title: 'Taxonomy' } },
      { path: 'users', name: ROUTE_NAMES.SHOP_USERS, component: loadView('UserPage'), meta: { title: 'Users' } },
      { path: 'shipping', name: ROUTE_NAMES.SHOP_SHIPPING, component: loadView('ShopShippingPage'), meta: { title: 'Shipping' } },
      { path: 'reports', name: ROUTE_NAMES.SHOP_REPORTS, component: loadView('ReportsPage'), meta: { title: 'Reports' } },
      { path: 'updates', name: ROUTE_NAMES.SHOP_UPDATES, component: loadView('UpdatesPage'), meta: { title: 'Policies' } },
      { path: 'updates/return-policies/:policy_id', name: ROUTE_NAMES.SHOP_UPDATE_LOGS, component: loadView('UpdateLogsPage'), meta: { title: 'Policy log' } },
      { path: 'consumer-paid', name: ROUTE_NAMES.SHOP_CONSUMER_PAID, component: loadView('OffsetPage'), meta: { title: 'Offset' } },
      { path: 'benefit-rules-override', name: ROUTE_NAMES.SHOP_BENEFIT_RULES_OVERRIDE, component: loadView('BenefitRulesOverridePage'), meta: { title: 'Benefit Rules' } },
      { path: 'shop-sync', name: ROUTE_NAMES.SHOP_SYNC, component: loadView('ShopSyncPage'), meta: { title: 'Sync' } },
      { path: 'api-keys', name: ROUTE_NAMES.SHOP_API_KEYS, component: loadView('ShopApiKeysPage'), meta: { title: 'API keys' } },
      { path: 'webhooks', name: ROUTE_NAMES.SHOP_WEBHOOKS, component: loadView('ShopWebhooksPage'), meta: { title: 'Webhooks' } },
      { path: 'shipbob', name: ROUTE_NAMES.SHOP_SHIPBOB, component: loadView('ShopShipBobPage'), meta: { title: 'ShipBob' } },
      { path: 'returns/bulk-actions', name: ROUTE_NAMES.SHOP_BULK_RETURN_ACTIONS, component: loadView('BulkReturnActionsPage'), meta: { title: 'Bulk Return Actions' } },
      { path: 'concierge-recipe', name: ROUTE_NAMES.SHOP_CONCIERGE_RECIPE, component: loadView('ConciergeRecipePage'), meta: { title: 'Concierge' } },
      { path: 'csp-domains', name: ROUTE_NAMES.SHOP_CSP_DOMAINS, component: loadView('ShopCspDomainsPage'), meta: { title: 'CSP' } },
      { path: 'settings-audit', name: ROUTE_NAMES.SHOP_SETTINGS_AUDIT, component: loadView('ShopSettingsAuditPage'), meta: { title: 'Audit' } },
      { path: 'graphiql', name: ROUTE_NAMES.SHOP_GRAPHIQL, component: loadView('ShopGraphiqlPage'), meta: { title: 'GraphiQL' } },
      { path: 'app-embeds', name: ROUTE_NAMES.SHOP_APP_EMBEDS, component: loadView('AppEmbedsPage'), meta: { title: 'Embeds' } },
      { path: 'web-pixel', name: ROUTE_NAMES.SHOP_WEB_PIXEL, component: loadView('WebPixelPage'), meta: { title: 'Web Pixel' } },
      { path: 'background-agents', name: ROUTE_NAMES.SHOP_BACKGROUND_AGENTS, component: loadView('BackgroundAgentsPage'), meta: { title: 'Agents' } },
      { path: 'observability', name: ROUTE_NAMES.SHOP_OBSERVABILITY, component: loadView('ObservabilityPage'), meta: { title: 'API Requests' } },
      { path: 'observability/requests/:loopRequestId', name: ROUTE_NAMES.SHOP_OBSERVABILITY_REQUEST, component: loadView('ObservabilityRequestPage'), meta: { title: 'API Request' } },
    ],
  },
  {
    path: '/early-access',
    name: ROUTE_NAMES.EARLY_ACCESS,
    component: loadView('EarlyAccessPage'),
    meta: { title: 'Early Access Program' },
  },
  {
    path: '/early-access/beta-features/new',
    name: ROUTE_NAMES.EARLY_ACCESS_BETA_FEATURE_NEW,
    component: loadView('EarlyAccessBetaFeatureNewPage'),
    meta: { title: 'Create Beta Feature' },
  },
  {
    path: '/early-access/beta-features/:id/edit',
    name: ROUTE_NAMES.EARLY_ACCESS_BETA_FEATURE_EDIT,
    component: loadView('EarlyAccessBetaFeatureEditPage'),
    meta: { title: 'Edit Beta Feature' },
  },
  {
    path: '/organizations',
    name: ROUTE_NAMES.ORGANIZATIONS,
    component: loadView('Organizations'),
    meta: { title: 'Search Organizations' },
  },
  {
    path: '/organization/:id',
    name: ROUTE_NAMES.ORGANIZATION,
    component: loadView('Organization'),
    meta: { title: 'Organization' },
  },
  {
    path: '/organization/:id/users',
    name: ROUTE_NAMES.ORGANIZATION_USERS,
    component: loadView('OrganizationUserPage'),
    meta: { title: 'Organization Users' },
  },
  {
    path: '/checkout-plus-billing',
    name: ROUTE_NAMES.CHECKOUT_PLUS_BILLING,
    component: loadView('CheckoutPlusBillingPage'),
    meta: { title: 'Checkout+ Billing' },
  },
  {
    path: '/offset-billing',
    redirect: { name: ROUTE_NAMES.CHECKOUT_PLUS_BILLING },
  },
  {
    path: '/merchant-notifications',
    name: ROUTE_NAMES.MERCHANT_NOTIFICATIONS,
    component: loadView('MerchantNotificationsPage'),
    meta: { title: 'Merchant Notifications' },
  },
  {
    path: '/merchant-notifications/create',
    name: ROUTE_NAMES.MERCHANT_NOTIFICATION_CREATE,
    component: loadView('MerchantNotificationCreatePage'),
    meta: { title: 'Create Merchant Notification' },
  },
  {
    path: '/merchant-notifications/:id/edit',
    name: ROUTE_NAMES.MERCHANT_NOTIFICATION_EDIT,
    component: loadView('MerchantNotificationEditPage'),
    meta: { title: 'Edit Merchant Notification' },
  },
  {
    path: '/merchant-notifications/:id',
    redirect: (to) => ({ path: `/merchant-notifications/${to.params.id as string}/edit` }),
  },
  {
    path: '/bluecherry-remediation',
    name: ROUTE_NAMES.BLUECHERRY_REMEDIATION,
    component: loadView('BluecherryRemediationPage'),
    meta: { title: 'Bluecherry Remediation' },
  },
  {
    path: '/audit-non-usd-fees',
    name: ROUTE_NAMES.AUDIT_NON_USD_FEES,
    component: loadView('AuditNonUsdFeesPage'),
    meta: { title: 'Audit Non-USD Fees' },
  },
  {
    path: '/bulk-destination-address-update',
    name: ROUTE_NAMES.BULK_DESTINATION_ADDRESS_UPDATE,
    component: loadView('BulkDestinationAddressUpdatePage'),
    meta: { title: 'Bulk Destination Address Update' },
  },
];

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

router.beforeEach(authNavigationGuard);

export function initDocumentTitle (): void {
  const shopStore = useShopStore();

  router.afterEach((to) => {
    applyDocumentTitle(to, resolveShopTitleContext(to, shopStore.shopData));
    toast.removeAllGroups();
    enablePageScroll();
  });

  watch(
    () => [shopStore.shopData?.name, shopStore.shopData?.id] as const,
    () => {
      // Only refresh the shop label while on a shop detail route. On other
      // routes the title may be set dynamically by the active page, and
      // reacting to shopData being cleared (e.g. when leaving a shop) would
      // clobber that dynamic title with the static route label.
      const currentRoute = router.currentRoute.value;
      if (!isShopDetailRoute(currentRoute)) {
        return;
      }

      applyDocumentTitle(
        currentRoute,
        resolveShopTitleContext(currentRoute, shopStore.shopData),
      );
    },
  );
}

export default router;
